import React, { ComponentProps, useEffect, useState } from 'react';
import { Modal, Text, ModalFooter, Button, Alert, useTable, Table } from '@datavant/uikit';
import {
    ChakraProvider,
    VStack,
    useToast,
    Box,
    Collapse,
    useDisclosure,
    Switch,
    HStack,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { trpc, tRPCProxyClient } from '../../../utils/trpc';
import {
    ColumnDef,
    ColumnFiltersState,
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    RowSelectionState,
} from '@tanstack/react-table';
import { NonEmbeddedImplementationStatus } from '../../../generated/request-manager-api';

export const EncounterFilterModal = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: ComponentProps<typeof Modal>['onClose'];
}) => {
    const toast = useToast();
    const { isOpen: isEditing, onToggle: toggleEditing } = useDisclosure();
    const { isOpen: isTableOpen, onToggle: toggleTable } = useDisclosure();
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [excludedTypes, setExcludedTypes] = useState<{ isActive: false; id: number }[]>([]);

    const encounterNameSearch = columnFilters.find(filter => filter.id === 'encounterTypeName')?.value;

    const utils = trpc.useUtils();

    const { data, refetch, isFetching } = trpc.autofulfill.getEncounterTypeFeedback.useQuery(
        {
            size: pagination.pageSize,
            page: pagination.pageIndex + 1,
            ...(typeof encounterNameSearch === 'string' ? { encounterNameSearch } : {}),
        },
        { keepPreviousData: true }
    );

    const { mutate, isLoading: isMutating } = trpc.autofulfill.mutateEncounterTypeFeedback.useMutation({
        onMutate: async () => {
            await utils.tenant.getTenant.cancel();
            const previousTenantData = utils.tenant.getTenant.getData();
            utils.tenant.getTenant.setData(undefined, old =>
                old && old !== null && old.digitalMetadata
                    ? {
                          ...old,
                          digitalMetadata: {
                              ...old.digitalMetadata,
                              onboardingStatus: NonEmbeddedImplementationStatus.ENCOUNTER_FEEDBACK_COMPLETED,
                          },
                      }
                    : null
            );

            // Return a context object with the snapshotted value so it can be used to reset the cache to a previous state
            // on error
            return { previousTenantData };
        },
        onSuccess: async () => {
            toast({
                title: 'Encounter types confirmed.',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
            await tRPCProxyClient.autofulfill.updateOnboardingStatus.mutate({
                status: NonEmbeddedImplementationStatus.ENCOUNTER_FEEDBACK_COMPLETED,
            });
            onClose();
        },
        onError: async () => {
            // Force a re-fetch of the tenant data on error
            await utils.tenant.getTenant.invalidate();

            toast({
                title: 'There was an error confirming encounter types. Please try again or reach out to customer service via help.',
                status: 'error',
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const isLoading = isFetching || isMutating;

    const columnHelper = createColumnHelper<NonNullable<typeof data>['items'][number]>();

    const columnDefs: ColumnDef<NonNullable<typeof data>['items'][number], any>[] = [
        columnHelper.display({
            id: 'activeSwitch',
            meta: {
                column: {
                    width: '1%',
                },
            },
            cell: ({ row }) => {
                return (
                    <Switch
                        isChecked={
                            excludedTypes.find(type => type.id === parseInt(row.id))?.isActive ??
                            row.original.isActive
                        }
                        isDisabled={!isEditing}
                        onChange={() =>
                            setExcludedTypes(excludedTypes => {
                                const maybeIndex = excludedTypes.findIndex(
                                    type => type.id === parseInt(row.id)
                                );

                                return maybeIndex === -1
                                    ? [...excludedTypes, { isActive: false, id: parseInt(row.id) }]
                                    : [
                                          ...excludedTypes.slice(0, maybeIndex),
                                          ...excludedTypes.slice(maybeIndex + 1),
                                      ];
                            })
                        }
                    />
                );
            },
        }),
        columnHelper.accessor(row => row.encounterTypeName, {
            id: 'encounterTypeName',
            meta: {
                filter: {
                    search: {
                        placeholder: 'Encounter Type',
                        type: 'search',
                    },
                },
            },
        }),
    ];

    const table = useTable({
        data: data?.items ?? [],
        columns: columnDefs,
        getCoreRowModel: getCoreRowModel(),
        onColumnFiltersChange: columnFilters => {
            setPagination(pagination => ({
                ...pagination,
                pageIndex: 0,
            }));
            setColumnFilters(columnFilters);
        },
        state: { pagination, columnFilters },
        filterFns: {},
        manualPagination: true,
        enableRowSelection: false,
        onPaginationChange: setPagination,
        rowCount: data?.total ?? undefined,
        getRowId: row => row.id.toString(),
    });

    const NotEditingBody = (
        <VStack gap={'16px'} alignItems={'flex-start'}>
            <Text.Contrast appearance={'medium'} size={'sm'}>
                Datavant has pre-selected the recommended encounter types based on your EMR and data
                extraction to optimize the AutoFulfill process.
            </Text.Contrast>
            <Button
                buttonAppearance={'ghost'}
                intent={'neutral'}
                rightIcon={<FontAwesomeIcon icon={isTableOpen ? faCaretUp : faCaretDown} />}
                onClick={toggleTable}
            >
                Default Encounter Types
            </Button>
            <Collapse in={isTableOpen}>
                <Table
                    table={table}
                    isLoading={isLoading}
                    columnDefs={columnDefs}
                    tableAppearance={'ghost'}
                    headerOptions={{
                        showResetButton: false,
                        showExportButton: false,
                        showRefreshButton: false,
                    }}
                ></Table>
            </Collapse>

            <Text.Contrast appearance={'medium'} size={'sm'}>
                We recommend using these default settings for the best results. Applying additional filters
                outside of this default may reduce provider yield and increase processing time.
            </Text.Contrast>
            <Alert
                description={
                    <VStack alignItems={'flex-start'} gap={0}>
                        <Box>
                            If you need to modify these default settings, please note that it may take up to 2
                            additional weeks to activate AutoFulfill.
                        </Box>
                        <Box>You can always adjust these settings later if needed.</Box>
                    </VStack>
                }
            />
        </VStack>
    );

    const EditingBody = (
        <VStack alignItems={'flex-start'} gap={'16px'}>
            <Alert
                status={'negative'}
                description={
                    'If you need to modify these default settings, please note that it may take up to 2 additional weeks to activate AutoFulfill. '
                }
            />
            <Table
                table={table}
                isLoading={isLoading}
                columnDefs={columnDefs}
                tableAppearance={'ghost'}
                onResetClick={refetch}
                headerOptions={{
                    showResetButton: true,
                    showExportButton: false,
                    showRefreshButton: false,
                    belowHeaderContent: (
                        <HStack pb={'2px'}>
                            <FontAwesomeIcon icon={faTriangleExclamation} color={'#87690B'} />
                            <Text.Status size={'xs'} appearance={'caution'}>
                                {`${excludedTypes.length} modification(s)`}
                            </Text.Status>
                        </HStack>
                    ),
                }}
            ></Table>
        </VStack>
    );

    return (
        <ChakraProvider>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                size={'md'}
                header={
                    <Text.Contrast appearance={'high'} size={'xl'}>
                        Verify Encounter Type Filter
                    </Text.Contrast>
                }
                body={!isEditing ? NotEditingBody : EditingBody}
                Footer={() => (
                    <ModalFooter
                        action={
                            <Button
                                intent={!isEditing ? 'prominent' : 'negative'}
                                isLoading={isLoading}
                                onClick={() => {
                                    mutate(
                                        excludedTypes.map(item => ({
                                            active: item.isActive,
                                            id: item.id,
                                        }))
                                    );
                                }}
                            >
                                {!isEditing ? 'Use Default Settings' : 'Proceed with Modifications'}
                            </Button>
                        }
                        cancel={
                            !isEditing ? (
                                <Button
                                    buttonAppearance={'ghost'}
                                    intent={'negative'}
                                    onClick={toggleEditing}
                                    isLoading={isLoading}
                                >
                                    Modify Settings
                                </Button>
                            ) : (
                                <></>
                            )
                        }
                        back={
                            <Button
                                buttonAppearance={'ghost'}
                                intent={'neutral'}
                                onClick={() => {
                                    if (!isEditing) {
                                        onClose();
                                    } else {
                                        toggleEditing();
                                        void refetch();
                                    }
                                }}
                            >
                                {!isEditing ? 'Close' : 'Back to Default Settings'}
                            </Button>
                        }
                    />
                )}
            />
        </ChakraProvider>
    );
};
